import React, { useEffect, Suspense } from "react";
import type { ReactNode } from "react";
import { Route, Routes } from "react-router-dom";
import { connect } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";

import * as actions from "../store/actions/index";

import ErrorBoundary from "../hoc/ErrorBoundary";
import { ToastProvider } from "../contexts/ToastContext";

import { withRouter } from "../shared/utility";
import DefaultLayout from "../components/layouts/DefaultLayout";
import Login from "../views/authentication/Login";
import ForgotPassword from "../views/authentication/ForgotPassword";
import ResetPassword from "../views/authentication/ResetPassword";
import { getPageTitle } from "../shared/routeTitles";
import type { AuthState } from "../types/auth";
import type { CartState } from "../types/orders";

const PUBLIC_AUTH_PATHS = ["/login", "/forgot-password", "/reset-password"];

interface AppProps {
  isAuthenticated: boolean;
  authCheckState: () => void;
  autchCheckCart: () => void;
  authCheckOrderDetails: () => void;
  router?: { location?: { pathname?: string } };
}
interface AppState {
  auth: AuthState;
  cart: CartState;
}

function App(props: AppProps) {
  const { autchCheckCart, authCheckOrderDetails, authCheckState } = props;

  useEffect(() => {
    authCheckState();
  }, [authCheckState]);

  useEffect(() => {
    autchCheckCart();
  }, [autchCheckCart]);

  useEffect(() => {
    authCheckOrderDetails();
  }, [authCheckOrderDetails]);

  let routes: ReactNode = null;

  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    if (!authToken) {
      const path = props.router?.location?.pathname ?? "/login";
      if (PUBLIC_AUTH_PATHS.includes(path)) {
        document.title = getPageTitle(path);
      } else {
        document.title = "Login | Flux Orders";
      }
    }
  }, [authToken, props.router?.location?.pathname]);

  if (authToken) {
    if (props.isAuthenticated) {
      routes = (
        <Routes>
          <Route path="*" element={<DefaultLayout />} />
        </Routes>
      );
    }
  } else {
    routes = (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <ToastProvider>
      <div className="h-full">
        <ErrorBoundary>
          <Suspense fallback={<p>Loading...</p>}>{routes}</Suspense>
        </ErrorBoundary>
      </div>
    </ToastProvider>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    isAuthenticated: state.auth.authToken !== null,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>) => {
  return {
    authCheckState: () => dispatch(actions.authCheckState()),
    autchCheckCart: () => dispatch(actions.autchCheckCart()),
    authCheckOrderDetails: () => dispatch(actions.authCheckOrderDetails()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
